import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { tokenManager } from '../../lib/api'
import TimelineManager from '../../components/TimelineManager'
import { GanttStage } from '../../components/GanttChart'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

const ProcessView: React.FC = () => {
  const { programId } = useParams<{ programId: string }>()
  const [acquisitionPlan, setAcquisitionPlan] = useState<any>(null)
  const [selectionStages, setSelectionStages] = useState<GanttStage[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      if (!programId) return

      try {
        const token = tokenManager.getAccessToken()
        if (!token) return

        // Fetch acquisition plan
        const planResponse = await fetch(`${API_BASE_URL}/api/programs/${programId}/acquisition-plan/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        })
        if (planResponse.ok) {
          const planData = await planResponse.json()
          setAcquisitionPlan(planData)
        }

        // Fetch selection stages
        const stagesResponse = await fetch(`${API_BASE_URL}/api/selection-stages/?program=${programId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        })
        if (stagesResponse.ok) {
          const stagesData = await stagesResponse.json()
          // API returns paginated data, extract the results array
          setSelectionStages(stagesData.results || stagesData || [])
        }

        setLoading(false)
      } catch (error) {
        console.error('Error fetching data:', error)
        setLoading(false)
      }
    }

    fetchData()
  }, [programId])

  const handleStageUpdate = async (stageId: number, updates: Partial<GanttStage>) => {
    try {
      const token = tokenManager.getAccessToken()
      const response = await fetch(`${API_BASE_URL}/api/selection-stages/${stageId}/`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates)
      })
      
      if (response.ok) {
        const updatedStage = await response.json()
        setSelectionStages(prev => 
          prev.map(s => s.id === stageId ? updatedStage : s)
        )
      }
    } catch (error) {
      console.error('Error updating selection stage:', error)
    }
  }

  const handleStartDateChange = async (date: string) => {
    try {
      const token = tokenManager.getAccessToken()
      const response = await fetch(`${API_BASE_URL}/api/programs/${programId}/acquisition-plan/`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ start_date: date })
      })
      
      if (response.ok) {
        const updatedPlan = await response.json()
        setAcquisitionPlan(updatedPlan)
      }
    } catch (error) {
      console.error('Error updating acquisition plan:', error)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <TimelineManager
        acquisitionPlan={acquisitionPlan}
        selectionStages={selectionStages}
        onStageUpdate={handleStageUpdate}
        onStartDateChange={handleStartDateChange}
      />
    </div>
  )
}

export default ProcessView

